import { useState } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";

interface PricingTabProps {
  propertyId: string;
  existingBookings?: {
    check_in_date: string;
    check_out_date: string;
    status: string;
  }[];
}

export const PricingTab = ({ propertyId, existingBookings = [] }: PricingTabProps) => {
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [price, setPrice] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const isDateBooked = (date: Date) => {
    return existingBookings.some(booking => {
      const checkIn = new Date(booking.check_in_date);
      const checkOut = new Date(booking.check_out_date);
      return date >= checkIn && date <= checkOut;
    });
  };

  const handleUpdatePrice = async () => {
    if (!selectedDate || !price) return;

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('pricing_strategies')
        .upsert({
          property_id: propertyId,
          name: `Custom price for ${format(selectedDate, 'yyyy-MM-dd')}`,
          rules: {
            dates: [format(selectedDate, 'yyyy-MM-dd')],
            price: parseFloat(price)
          },
          start_date: format(selectedDate, 'yyyy-MM-dd'),
          end_date: format(selectedDate, 'yyyy-MM-dd')
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Price has been updated successfully",
      });
      setSelectedDate(undefined);
      setPrice("");
    } catch (error) {
      console.error('Error updating price:', error);
      toast({
        title: "Error",
        description: "Failed to update price. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6 p-4">
      <div className="flex justify-center">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={setSelectedDate}
          disabled={(date) => isDateBooked(date) || date < new Date()}
          className="rounded-md border w-full max-w-[400px] mx-auto bg-white"
        />
      </div>

      {selectedDate && (
        <div className="space-y-4 max-w-md mx-auto">
          <div className="space-y-2">
            <Label htmlFor="price">Price for {format(selectedDate, 'MMMM dd, yyyy')}</Label>
            <Input
              id="price"
              type="number"
              min="0"
              step="0.01"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="Enter price"
            />
          </div>

          <Button
            onClick={handleUpdatePrice}
            disabled={isLoading || !price}
            className="w-full"
          >
            {isLoading ? "Updating..." : "Update Price"}
          </Button>
        </div>
      )}
    </div>
  );
};